import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

export interface SheetProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Sticky action row at the bottom of the sheet. */
  footer?: React.ReactNode;
}

/**
 * Slide-over editor used by every console list. Keeps the list visible behind
 * it, so a technician never loses their place while editing.
 */
export const Sheet: React.FC<SheetProps> = ({
  isOpen,
  title,
  subtitle,
  onClose,
  children,
  footer,
}) => (
  <AnimatePresence>
    {isOpen && (
      <div className="fixed inset-0 z-50 flex justify-end">
        <motion.button
          type="button"
          aria-label="Close"
          className="absolute inset-0 bg-[#1A1A1A]/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        />

        <motion.aside
          className="relative z-10 flex h-full w-full max-w-[460px] flex-col bg-white shadow-[-8px_0_32px_rgba(0,0,0,0.18)]"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        >
          <header className="flex items-start justify-between gap-4 border-b border-[#E0E0E0] px-6 py-5">
            <div>
              <h2 className="font-display text-2xl text-[#333333] leading-tight">{title}</h2>
              {subtitle && <p className="mt-1 text-sm text-[#666666]">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#333333]"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

          {footer && (
            <footer className="flex items-center justify-end gap-3 border-t border-[#E0E0E0] bg-[#FAFAFA] px-6 py-4">
              {footer}
            </footer>
          )}
        </motion.aside>
      </div>
    )}
  </AnimatePresence>
);

export default Sheet;
